import { useState, useEffect, useMemo } from "react";
import LinearProgress from "@mui/material/LinearProgress";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Tabs,
  Tab,
  TextField,
  Tooltip,
  DataGrid,
  InputAdornment,
} from "../../../../../components";
import { labelKeysRevisaoIA } from "../../constantes";
import {
  NcmService,
  NfSaidaItensService,
  notification,
  ProdutosService,
} from "../../../../../services";
import { responseErros } from "../../../../../utils";

const nfSaidaItensService = new NfSaidaItensService();
const produtosService = new ProdutosService();
const ncmService = new NcmService();

const camposCorrigiveis = {
  NCM: "ncm",
  CFOP: "cfop",
  SITUACAO_TRIBUTARIA: "situacao_tributaria",
};

const CAMPOS_VALIDOS = Object.values(camposCorrigiveis);

const severidadeConfig = {
  ALTA: {
    label: "Crítico",
    bgcolor: "#ffebee",
    color: "#c62828",
    border: "#ef9a9a",
  },
  MEDIA: {
    label: "Atenção",
    bgcolor: "#fff8e1",
    color: "#e65100",
    border: "#ffcc80",
  },
  BAIXA: {
    label: "Sugestão",
    bgcolor: "#e3f2fd",
    color: "#1565c0",
    border: "#90caf9",
  },
};

function SeveridadeChip({ severidade }) {
  const cfg = severidadeConfig[String(severidade).toUpperCase()] || {
    label: severidade,
    bgcolor: "grey.100",
    color: "text.primary",
    border: "grey.300",
  };
  return (
    <Chip
      label={cfg.label}
      size="small"
      sx={{
        fontFamily: "'Montserrat', 'sans-serif'",
        bgcolor: cfg.bgcolor,
        color: cfg.color,
        border: `1px solid ${cfg.border}`,
        fontWeight: 700,
        fontSize: 11,
      }}
    />
  );
}

function ScoreBadge({ score }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        border: "2px solid rgba(255,255,255,0.4)",
        borderRadius: 2,
        px: 2,
        py: 0.5,
        minWidth: 72,
      }}
    >
      <Typography
        variant="h4"
        sx={{ fontWeight: 900, color: "common.white", lineHeight: 1 }}
      >
        {score}
      </Typography>
      <Typography
        variant="caption"
        sx={{ color: "rgba(255,255,255,0.8)", fontSize: 10 }}
      >
        score fiscal
      </Typography>
    </Box>
  );
}

function SummaryCard({ icon, count, label, bgcolor, color, border }) {
  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        border: `1.5px solid ${border}`,
        borderRadius: 2,
        px: 2,
        py: 1.5,
        bgcolor,
      }}
    >
      <Box sx={{ color, fontSize: 20, display: "flex" }}>{icon}</Box>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 800, color, lineHeight: 1 }}>
          {count}
        </Typography>
        <Typography
          variant="caption"
          sx={{ color, opacity: 0.8, fontWeight: 500 }}
        >
          {label}
        </Typography>
      </Box>
    </Box>
  );
}

function buildFlatRows(itens = [], nfSaidaItens = []) {
  const rows = [];
  itens.forEach((item) => {
    const baseItem = nfSaidaItens[item.indice] || {};
    const codigoItem = baseItem.codigo
      ? `#${baseItem.codigo}`
      : `#${item.indice + 1}`;
    (item.anomalias || []).forEach((anom, j) => {
      rows.push({
        id: `${item.indice}-${j}`,
        indice: item.indice,
        codigoItem,
        descricaoItem: item.descricao,
        severidade: String(anom.severidade || "").toUpperCase(),
        campo: anom.campo,
        valorAtual: anom.valor_enviado,
        sugestaoIA: anom.valor_esperado,
        justificativa: anom.sugestao,
        fundamentacao: anom.fundamentacao,
        anomalia: anom.anomalia,
        corrigivel: CAMPOS_VALIDOS.includes(anom.campo),
      });
    });
  });
  return rows;
}

export default function RevisaoFiscalDialog({
  open,
  setOpen,
  revisaoFiscalAI = {},
  nfSaidaEntity,
  setNfSaidaProdutos,
}) {
  const itens = revisaoFiscalAI.itens || [];
  const nfSaidaItens = nfSaidaEntity?.nfSaidaItens || [];

  const flatRows = useMemo(
    () => buildFlatRows(itens, nfSaidaItens),
    [itens, nfSaidaItens],
  );

  const [abaAtiva, setAbaAtiva] = useState("todos");
  const [busca, setBusca] = useState("");
  const [selecionados, setSelecionados] = useState([]);

  useEffect(() => {
    if (open) {
      setAbaAtiva("todos");
      setBusca("");
      setSelecionados(flatRows.map((r) => r.id));
    }
  }, [open, revisaoFiscalAI]);

  useEffect(() => {
    if (open) {
      setSelecionados(flatRows.map((r) => r.id));
    }
  }, [flatRows]);

  function handleClose() {
    setOpen(false);
  }

  const totalCriticos = flatRows.filter((r) => r.severidade === "ALTA").length;
  const totalAtencao = flatRows.filter((r) => r.severidade === "MEDIA").length;
  const totalSugestoes = flatRows.filter(
    (r) => r.severidade === "BAIXA",
  ).length;
  const totalCorrigiveisAtivos = selecionados.filter((id) => {
    const row = flatRows.find((r) => r.id === id);
    return row?.corrigivel;
  }).length;

  const scoreFiscal = Math.round((revisaoFiscalAI.score_geral || 0) * 100);

  const rowsFiltradas = useMemo(() => {
    let base = flatRows;
    if (abaAtiva === "criticos")
      base = base.filter((r) => r.severidade === "ALTA");
    else if (abaAtiva === "atencao")
      base = base.filter((r) => r.severidade === "MEDIA");
    else if (abaAtiva === "sugestoes")
      base = base.filter((r) => r.severidade === "BAIXA");

    if (busca.trim()) {
      const q = busca.toLowerCase();
      base = base.filter((r) => {
        const descricao = String(r.descricaoItem || "").toLowerCase();
        const codigo = String(r.codigoItem || "").toLowerCase();
        const campoLabel = String(
          labelKeysRevisaoIA[r.campo] || r.campo || "",
        ).toLowerCase();
        const valorAtual = String(r.valorAtual || "").toLowerCase();
        return (
          descricao.includes(q) ||
          codigo.includes(q) ||
          campoLabel.includes(q) ||
          valorAtual.includes(q)
        );
      });
    }
    return base;
  }, [flatRows, abaAtiva, busca]);

  const buscarNcms = async (filtro) => {
    const result = await ncmService.getAllAvancado({ ...filtro, limite: 20 });
    if (!result.isAxiosError) return result.data.rows;
    responseErros(result);
  };

  function aplicarCorrecao(anomalias, acc, context) {
    for (const anomalia of anomalias) {
      const valor = String(anomalia.valor_esperado);
      if (anomalia.campo === camposCorrigiveis.NCM) {
        const codigo = valor.split(" ")[0];
        acc.ncmCodigo = codigo;
        acc.ncm = codigo;
        context.existChangeNcm = true;
      }
      if (anomalia.campo === camposCorrigiveis.CFOP) acc.cfop = valor;
      if (anomalia.campo === camposCorrigiveis.SITUACAO_TRIBUTARIA)
        acc.tributosIcmsCsosn = valor;
    }
    return acc;
  }

  async function aplicarCorrecoesItens() {
    const rowsSel = flatRows.filter(
      (r) => selecionados.includes(r.id) && r.corrigivel,
    );

    if (!rowsSel.length) {
      return notification.alertaGenericos(
        "Nenhuma correção aplicável selecionada. Apenas NCM, CFOP ou Situação Tributária podem ser corrigidos automaticamente.",
      );
    }

    const contexto = { existChangeNcm: false };
    const porIndice = {};
    for (const row of rowsSel) {
      if (!porIndice[row.indice]) porIndice[row.indice] = [];
      porIndice[row.indice].push({
        campo: row.campo,
        valor_esperado: row.sugestaoIA,
      });
    }

    const itensAcorrigir = Object.entries(porIndice)
      .map(([indice, correcoes]) => {
        const base = nfSaidaItens[Number(indice)];
        if (!base) return null;
        return {
          id: base.id,
          produtoId: base.produtoId,
          indice: Number(indice),
          ...aplicarCorrecao(correcoes, {}, contexto),
        };
      })
      .filter(Boolean);

    await atualizarCorrecaoItens(itensAcorrigir, contexto.existChangeNcm);
  }

  async function atualizarCorrecaoItens(
    itensAcorrigir = [],
    existChangeNcm = false,
  ) {
    try {
      const result = await nfSaidaItensService.atualizarLista(
        itensAcorrigir.map(({ id, ncm, cfop, tributosIcmsCsosn }) => ({
          id,
          ...(ncm !== undefined && { ncm }),
          ...(cfop !== undefined && { cfop }),
          ...(tributosIcmsCsosn !== undefined && { tributosIcmsCsosn }),
        })),
      );
      if (result.isAxiosError) return responseErros(result);
      atualizarEstadoLocal(itensAcorrigir);
      if (existChangeNcm) await atualizarNcmProdutos(itensAcorrigir);
      notification.alteracaoSucesso();
      handleClose();
    } catch {
      notification.erroGenericos(
        "Erro ao aplicar correções. Verifique os dados e tente novamente.",
      );
    }
  }

  function atualizarEstadoLocal(itensCorrigidos) {
    nfSaidaEntity.nfSaidaItens = nfSaidaItens.map((item, idx) => {
      const atualizado = itensCorrigidos.find((i) => i.indice === idx);
      return atualizado ? { ...item, ...atualizado } : item;
    });
    setNfSaidaProdutos((prev) =>
      prev.map((item, idx) => {
        const atualizado = itensCorrigidos.find((i) => i.indice === idx);
        return atualizado ? { ...item, ...atualizado } : item;
      }),
    );
  }

  async function atualizarNcmProdutos(itens) {
    const itensNcm = itens.filter((i) => i.ncmCodigo);
    if (!itensNcm.length) return;
    const codigos = [...new Set(itensNcm.map((i) => i.ncmCodigo))];
    const ncms = await buscarNcms({ codigo: codigos });
    const atualizados = itensNcm
      .map((item) => {
        const encontrado = ncms.find((n) => n.codigo === item.ncmCodigo);
        return encontrado ? { ...item, ncmId: encontrado.id } : item;
      })
      .filter(({ ncmId }) => typeof ncmId === "number");
    if (atualizados.length) await produtosService.atualizarLista(atualizados);
  }

  const colunas = [
    {
      field: "severidade",
      headerName: "Severidade",
      flex: 130,
      sortable: false,
      renderCell: (params) => <SeveridadeChip severidade={params.value} />,
    },
    {
      field: "codigoItem",
      headerName: "Item",
      flex: 200,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Typography
            variant="body2"
            gutterBottom
            className="mb-0 fw-normal"
            sx={{
              fontFamily: "'Montserrat', 'sans-serif'",
            }}
          >
            {params.value}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: 180,
              fontFamily: "'Montserrat', 'sans-serif'",
            }}
          >
            {params.row.descricaoItem}
          </Typography>
        </Box>
      ),
    },
    {
      field: "campo",
      headerName: "Campo",
      flex: 160,
      sortable: false,
      valueGetter: (params) => labelKeysRevisaoIA[params.value] || params.value,
    },
    {
      field: "valorAtual",
      headerName: "Valor atual",
      flex: 130,
      sortable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <Box
          sx={{
            border: "1.5px solid",
            borderColor: "grey.300",
            borderRadius: 4,
            px: 1.5,
            py: 0.25,
            fontSize: 12,
            color: "text.secondary",
            minWidth: 32,
            textAlign: "center",
          }}
        >
          {params.value || "—"}
        </Box>
      ),
    },
    {
      field: "sugestaoIA",
      headerName: "Sugestão IA",
      flex: 130,
      sortable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <Chip
          label={params.value || "—"}
          size="small"
          sx={{
            fontFamily: "'Montserrat', 'sans-serif'",
            bgcolor: "#1b5e20",
            color: "common.white",
            fontWeight: 700,
            fontSize: 12,
          }}
        />
      ),
    },
    {
      field: "justificativa",
      headerName: "Justificativa",
      flex: 380,
      sortable: false,
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            overflow: "hidden",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              flex: 1,
              fontFamily: "'Montserrat', 'sans-serif'",
            }}
          >
            {params.value}
          </Typography>
          {params.row.fundamentacao && (
            <Tooltip title={params.row.fundamentacao} arrow placement="top">
              <Chip
                label="Base legal"
                size="small"
                sx={{
                  fontSize: 10,
                  flexShrink: 0,
                  cursor: "help",
                  fontFamily: "'Montserrat', 'sans-serif'",
                }}
              />
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  const hasSelecionadosCorrigiveis = selecionados.some((id) => {
    const row = flatRows.find((r) => r.id === id);
    return row?.corrigivel;
  });

  return (
    <Dialog open={!!open} onClose={handleClose} fullWidth maxWidth="lg">
      <Box
        sx={{
          background: "linear-gradient(135deg, #3949ab 0%, #1e88e5 100%)",
          px: 3,
          pt: 2.5,
          pb: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
          <Box
            sx={{
              bgcolor: "rgba(255,255,255,0.15)",
              borderRadius: 2,
              p: 1,
              display: "flex",
              mt: 0.5,
            }}
          >
            <i
              className="ph-fill ph-sparkle"
              style={{ fontSize: 22, color: "white" }}
              aria-hidden
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography
                variant="h6"
                sx={{ color: "common.white", fontWeight: 700 }}
              >
                Revisão Fiscal AI
              </Typography>
              {nfSaidaEntity.numeroDocumento && (
                <>
                  <Typography sx={{ color: "rgba(255,255,255,0.5)" }}>
                    ·
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{ color: "common.white", fontWeight: 700 }}
                  >
                    NF #{nfSaidaEntity.numeroDocumento}
                  </Typography>
                </>
              )}
            </Box>
            <Typography
              variant="body2"
              sx={{ color: "rgba(255,255,255,0.75)", mt: 0.25 }}
            >
              {flatRows.length} achado{flatRows.length !== 1 ? "s" : ""} em{" "}
              {itens.length} {itens.length === 1 ? "item" : "itens"} · Análise
              concluída
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <ScoreBadge score={scoreFiscal} />
            <Button
              onClick={handleClose}
              variant="text"
              size="small"
              sx={{ minWidth: 0, p: 0.5 }}
            >
              <i
                className="ph ph-x"
                style={{ fontSize: 20, color: "white" }}
                aria-hidden
              />
            </Button>
          </Box>
        </Box>
        <Box sx={{ mt: 2 }}>
          <LinearProgress
            variant="determinate"
            value={scoreFiscal}
            sx={{
              height: 5,
              borderRadius: 0,
              bgcolor: "rgba(255,255,255,0.2)",
              "& .MuiLinearProgress-bar": { bgcolor: "rgba(255,255,255,0.85)" },
            }}
          />
        </Box>
      </Box>

      <DialogContent sx={{ p: 2.5, bgcolor: "#f5f6fa" }}>
        <Box sx={{ display: "flex", gap: 1.5, mb: 2.5 }}>
          <SummaryCard
            icon={
              <i
                className="ph-fill ph-warning-circle"
                style={{ color: "#c62828" }}
              />
            }
            count={totalCriticos}
            label="Críticos"
            bgcolor="#ffebee"
            color="#c62828"
            border="#ef9a9a"
          />
          <SummaryCard
            icon={
              <i className="ph-fill ph-warning" style={{ color: "#e65100" }} />
            }
            count={totalAtencao}
            label="Atenção"
            bgcolor="#fff8e1"
            color="#e65100"
            border="#ffcc80"
          />
          <SummaryCard
            icon={
              <i
                className="ph-fill ph-check-circle"
                style={{ color: "#1565c0" }}
              />
            }
            count={totalSugestoes}
            label="Sugestões"
            bgcolor="#e3f2fd"
            color="#1565c0"
            border="#90caf9"
          />
          <SummaryCard
            icon={
              <i
                className="ph-fill ph-check-square"
                style={{ color: "#1b5e20" }}
              />
            }
            count={totalCorrigiveisAtivos}
            label="correções selecionadas"
            bgcolor="#e8f5e9"
            color="#1b5e20"
            border="#a5d6a7"
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            mb: 1.5,
            bgcolor: "common.white",
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            px: 1,
          }}
        >
          <Tabs
            value={abaAtiva}
            onChange={(_, v) => setAbaAtiva(v)}
            textColor="primary"
            indicatorColor="primary"
            sx={{ flex: 1, minHeight: 44 }}
          >
            <Tab
              value="todos"
              label={`TODOS (${flatRows.length})`}
              sx={{ fontSize: 12, fontWeight: 700, minHeight: 44 }}
            />
            <Tab
              value="criticos"
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  CRÍTICOS
                  {totalCriticos > 0 && (
                    <Box
                      sx={{
                        bgcolor: "error.main",
                        color: "common.white",
                        borderRadius: "50%",
                        width: 18,
                        height: 18,
                        fontSize: 10,
                        fontWeight: 700,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {totalCriticos}
                    </Box>
                  )}
                </Box>
              }
              sx={{ fontSize: 12, fontWeight: 700, minHeight: 44 }}
            />
            <Tab
              value="atencao"
              label={`ATENÇÃO (${totalAtencao})`}
              sx={{ fontSize: 12, fontWeight: 700, minHeight: 44 }}
            />
            <Tab
              value="sugestoes"
              label={`SUGESTÕES (${totalSugestoes})`}
              sx={{ fontSize: 12, fontWeight: 700, minHeight: 44 }}
            />
          </Tabs>

          <TextField
            size="small"
            placeholder="Buscar por campo, item ou código..."
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            sx={{ width: 280 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <i
                    className="ph ph-magnifying-glass"
                    style={{ fontSize: 16 }}
                    aria-hidden
                  />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Box
          sx={{
            bgcolor: "common.white",
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            overflow: "hidden",
          }}
        >
          <DataGrid
            rows={rowsFiltradas}
            columns={colunas}
            rowCount={rowsFiltradas.length}
            disableAcoes
            noFilter
            nonPaginated
            checkboxSelection
            disableRowSelectionOnClick
            selectionModel={selecionados}
            onRowSelectionModelChange={setSelecionados}
            getRowId={(row) => row.id}
          />
        </Box>

        {revisaoFiscalAI.resumo_geral && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              bgcolor: "#f0f4ff",
              border: "1px solid #c5cae9",
              borderRadius: 2,
              display: "flex",
              gap: 1.5,
            }}
          >
            <i
              className="ph-fill ph-sparkle"
              style={{
                fontSize: 18,
                color: "#3949ab",
                flexShrink: 0,
                marginTop: 2,
              }}
              aria-hidden
            />
            <Box>
              <Typography
                variant="subtitle2"
                sx={{ color: "#3949ab", mb: 0.5 }}
              >
                Como a IA chegou nessas sugestões
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {revisaoFiscalAI.resumo_geral}
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 2.5,
          py: 1.5,
          borderTop: "1px solid",
          borderColor: "divider",
        }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ mr: 2 }}>
          {selecionados.length} de {flatRows.length} ajuste(s) selecionado(s)
        </Typography>

        <Button onClick={handleClose} variant="text" color="inherit">
          Cancelar
        </Button>

        <Button
          onClick={aplicarCorrecoesItens}
          variant="contained"
          color="primary"
          disableElevation
          disabled={!hasSelecionadosCorrigiveis}
        >
          Aplicar Selecionados
        </Button>
      </DialogActions>
    </Dialog>
  );
}
